import { useCallback, useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    FormControl,
    InputLabel,
    MenuItem,
    OutlinedInput,
    Pagination,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import axios from 'axios';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { Link, useSearchParams } from 'react-router';

import { asGermanDateTime } from 'src/common/date';
import { safeDate } from 'src/common/helpers';
import { useAppDispatch, useAppSelector } from 'src/redux-app-hooks';
import { loadOnsiteTemplates } from './onsite-visit.slice';
import { OnsiteVisitDto, OnsiteVisitSearchDto, OnsiteVisitsResponseDto } from './onsite-visit.dto';
import { admiOnsiteVisitService } from './onsite-visit.service';

function toDayjs(params: URLSearchParams, key: string): Dayjs | null {
    const value = params.get(key);
    return value ? dayjs(value) : null;
}

function OnsiteVisitListPage() {
    const [onsiteVisits, setOnsiteVisits] = useState<OnsiteVisitsResponseDto>();
    const [isLoading, setIsLoading] = useState(true);
    const [loadFailed, setLoadFailed] = useState(false);
    const [retryCount, setRetryCount] = useState(0);
    const [visitSearchParams, setVisitSearchParams] = useSearchParams();

    // Seeded from the URL rather than synced into it on mount, so the first render already has
    // the filters the URL asked for and the sync below is a no-op until the URL next changes.
    const [status, setStatus] = useState<string | null>(() => visitSearchParams.get('status') ?? '');
    const [selectedTemplate, setSelectedTemplate] = useState<string | null>(
        () => visitSearchParams.get('templateId') ?? '',
    );
    const [visitDateFrom, setVisitDateFrom] = useState<Dayjs | null>(() => toDayjs(visitSearchParams, 'visitDateFrom'));
    const [visitDateTo, setVisitDateTo] = useState<Dayjs | null>(() => toDayjs(visitSearchParams, 'visitDateTo'));
    const [syncedSearch, setSyncedSearch] = useState<string>(() => visitSearchParams.toString());

    const dispatch = useAppDispatch();
    const templates = useAppSelector((state) => state.onsiteVisit.templates);
    const { t } = useTranslation();

    // Only fills the template filter -- the list does not wait on it.
    useEffect(() => {
        dispatch(loadOnsiteTemplates());
    }, [dispatch]);

    /**
     * Put the paging defaults in the URL before anything reads them.
     *
     * This used to be written into the same effect that released the load, gated behind a `ready`
     * flag, and in production every page view then issued two identical requests for the list --
     * every pair of spans in 30 days of Sentry data shows it, ~20 ms apart, both running the full
     * 10 s (GEN-746). The likely mechanism is the router navigation and the `ready` state landing
     * in separate commits, but jsdom will not reproduce the ordering, so treat that as unproven.
     * What is certain is the shape: two triggers for one fetch, and no way to cancel the loser.
     * Here the defaults are their own effect, so the load effect has a single trigger -- the
     * params -- and aborts whatever it supersedes.
     */
    useEffect(() => {
        if (visitSearchParams.has('page')) return;

        setVisitSearchParams(
            (prev) => {
                const params = new URLSearchParams(prev);
                params.set('page', '1');
                params.set('size', '10');
                params.set('sort', 'visitDate,desc');
                return params;
            },
            { replace: true },
        );
    }, [visitSearchParams, setVisitSearchParams]);

    // The filter inputs mirror the URL, and the URL can change under them -- a search, the browser
    // back button. React's "adjusting state when a prop changes" recipe: do it during render
    // rather than in an effect, so they never paint a frame out of step with the URL they mirror.
    const currentSearch = visitSearchParams.toString();
    if (syncedSearch !== currentSearch) {
        setSyncedSearch(currentSearch);
        setSelectedTemplate(visitSearchParams.get('templateId') ?? '');
        setStatus(visitSearchParams.get('status') ?? '');
        setVisitDateFrom(toDayjs(visitSearchParams, 'visitDateFrom'));
        setVisitDateTo(toDayjs(visitSearchParams, 'visitDateTo'));
    }

    const loadOnsiteVisits = useCallback(
        async (signal: AbortSignal) => {
            setIsLoading(true);
            setLoadFailed(false);

            const searchParams: OnsiteVisitSearchDto = {
                page: Number(visitSearchParams.get('page')) || 1,
                size: Number(visitSearchParams.get('size')) || 10,
                sort: visitSearchParams.get('sort') || 'visitDate,desc',
                templateId: visitSearchParams.get('templateId')?.trim() || null,
                status: visitSearchParams.get('status')?.trim() || null,
                visitDateFrom: safeDate(visitSearchParams.get('visitDateFrom')),
                visitDateTo: safeDate(visitSearchParams.get('visitDateTo')),
            };

            try {
                const response = await admiOnsiteVisitService.getOnsiteVisits(searchParams, signal);
                setOnsiteVisits(response);
            } catch (error) {
                // A superseded request is not a failure, and its state belongs to nobody: the
                // request that replaced it is still in flight and owns the spinner.
                if (axios.isCancel(error) || signal.aborted) return;

                console.error(error);
                setLoadFailed(true);
            }

            setIsLoading(false);
        },
        [visitSearchParams],
    );

    useEffect(() => {
        // Wait for the defaults above; the params are what this effect reads.
        if (!visitSearchParams.has('page')) return;

        const controller = new AbortController();
        // Fetching from an effect necessarily flips the spinner on synchronously; the alternative
        // is a frame of stale rows under a page the user has already moved off (same reason as
        // AdmiMap.tsx:135).
        // eslint-disable-next-line react-hooks/set-state-in-effect
        loadOnsiteVisits(controller.signal);

        // Paging or searching again cancels the request it supersedes, rather than racing it.
        return () => controller.abort();
    }, [visitSearchParams, retryCount, loadOnsiteVisits]);

    function changePage(page: number) {
        setVisitSearchParams((prev) => {
            const params = new URLSearchParams(prev);
            params.set('page', page.toString());
            return params;
        });
    }

    function handleSearch() {
        setVisitSearchParams(
            (prev) => {
                const params = new URLSearchParams(prev);
                params.set('page', '1');

                if (selectedTemplate) params.set('templateId', selectedTemplate);
                else params.delete('templateId');

                if (status) params.set('status', status);
                else params.delete('status');

                if (visitDateFrom) params.set('visitDateFrom', visitDateFrom.format('YYYY-MM-DD'));
                else params.delete('visitDateFrom');

                if (visitDateTo) params.set('visitDateTo', visitDateTo.format('YYYY-MM-DD'));
                else params.delete('visitDateTo');

                return params;
            },
            { replace: true },
        );
    }

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box>
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={2}
                    useFlexGap
                    sx={{ alignItems: 'flex-start', flexWrap: 'wrap', mt: 2 }}
                >
                    <FormControl sx={{ minWidth: 200, flex: 1 }}>
                        <InputLabel id="template-label" shrink>
                            {t('Template')}
                        </InputLabel>
                        <Select
                            labelId="template-label"
                            label={t('Template')}
                            value={selectedTemplate ?? ''}
                            onChange={(e) => setSelectedTemplate(e.target.value || null)}
                            input={<OutlinedInput label={t('Template')} size="small" notched />}
                        >
                            <MenuItem value="">{t('All Templates')}</MenuItem>
                            {templates.map((template) => (
                                <MenuItem key={template.id} value={template.id}>
                                    {template.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl sx={{ minWidth: 200, flex: 1 }}>
                        <InputLabel id="status-label" shrink>
                            {t('Status')}
                        </InputLabel>
                        <Select
                            labelId="status-label"
                            label={t('Status')}
                            value={status ?? ''}
                            onChange={(e) => setStatus(e.target.value || null)}
                            input={<OutlinedInput label={t('Status')} size="small" notched />}
                        >
                            <MenuItem value="">{t('All Statuses')}</MenuItem>
                            <MenuItem value="open">{t('Open_adjective')}</MenuItem>
                            <MenuItem value="taken">{t('Taken')}</MenuItem>
                            <MenuItem value="closed">{t('Closed')}</MenuItem>
                            <MenuItem value="cant_do">{t('Cannot do')}</MenuItem>
                            <MenuItem value="submitted">{t('Submitted')}</MenuItem>
                        </Select>
                    </FormControl>

                    <FormControl sx={{ minWidth: 200, flex: 1 }}>
                        <DatePicker
                            label={t('Visit Date From')}
                            value={visitDateFrom}
                            onChange={(newVal) => setVisitDateFrom(newVal)}
                            slotProps={{
                                textField: {
                                    fullWidth: true,
                                    size: 'small',
                                },
                            }}
                        />
                    </FormControl>

                    <FormControl sx={{ minWidth: 200, flex: 1 }}>
                        <DatePicker
                            label={t('Visit Date To')}
                            value={visitDateTo}
                            onChange={(newVal) => setVisitDateTo(newVal)}
                            slotProps={{
                                textField: {
                                    fullWidth: true,
                                    size: 'small',
                                },
                            }}
                        />
                    </FormControl>

                    <Box sx={{ minWidth: 150, alignSelf: 'stretch', display: 'flex', alignItems: 'flex-end' }}>
                        <Button variant="contained" onClick={handleSearch} fullWidth>
                            {t('Search')}
                        </Button>
                    </Box>
                </Stack>

                <Box sx={{ mt: 4 }}>
                    {loadFailed ? (
                        // Without this the table simply rendered empty, which reads as "there are
                        // no visits" rather than "we could not ask".
                        <Alert
                            severity="error"
                            action={
                                <Button color="inherit" size="small" onClick={() => setRetryCount((n) => n + 1)}>
                                    {t('Retry')}
                                </Button>
                            }
                        >
                            {t('Failed to load the visit list. Please try again.')}
                        </Alert>
                    ) : isLoading ? (
                        <CircularProgress />
                    ) : (
                        <Table>
                            <TableHead>
                                <TableRow
                                    sx={{
                                        backgroundColor: '#fbfcfe',
                                        height: 10,
                                    }}
                                >
                                    {['Title', 'Created at', 'Assigned to', 'Actions'].map((label) => (
                                        <TableCell
                                            key={label}
                                            sx={{
                                                fontWeight: 'bold',
                                                color: '#1e293b',
                                                py: 1,
                                                fontSize: '0.875rem',
                                            }}
                                        >
                                            {t(label)}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {onsiteVisits?.content.map((visit: OnsiteVisitDto) => (
                                    <TableRow key={visit.externalId}>
                                        <TableCell>{visit.title}</TableCell>
                                        <TableCell>{visit.visitDate && asGermanDateTime(visit.visitDate)}</TableCell>
                                        <TableCell>{visit.assignedToEmail}</TableCell>
                                        <TableCell>
                                            <Stack direction="row" spacing={1}>
                                                <Link to={`/onsite/onsite-visit/${visit.id}/submit`}>
                                                    <Button variant="outlined">{t('Submit Form')}</Button>
                                                </Link>
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </Box>

                {!loadFailed && !!onsiteVisits?.content.length && (
                    <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
                        <Pagination
                            variant="outlined"
                            count={onsiteVisits.totalPages}
                            page={Number(visitSearchParams.get('page') || 1)}
                            onChange={(_, page) => changePage(page)}
                        />
                    </Box>
                )}
            </Box>
        </LocalizationProvider>
    );
}

export default connect()(OnsiteVisitListPage);
